/**
 * Map derivation (D33 rules, D34 shape): `(bundle, state)` → {@link MapGraph}.
 *
 * @remarks
 * The knowledge rule: VISITED rooms are `visited`, rooms in
 * `mapDiscovery.revealedRoomIds` are `revealed`, and an exit from known
 * territory into the dark places an `unknown` stub carrying NO name — the map
 * shows what the player knows, never the authored world.
 *
 * Layout: cardinal exits (north/south/east/west) place rooms by breadth-first
 * walk from the active room, which always sits at the origin. The walk is
 * deterministic — rooms enter the queue in placement order, exits walk in
 * authored order — so the same session always yields the same graph. A link
 * whose endpoints do not land grid-adjacent, a non-cardinal exit, and an
 * outward exit all become annotations instead of edges: the map declines to
 * lie about geometry.
 *
 * D169 adds floors, on request: derived `{ isLevelled: true }`, an `up` or
 * `down` exit places its room at the SAME cell one floor up or down, and a
 * vertical link between two placed rooms is a lift the hologram draws rather
 * than an annotation. The flat graph (the default, and the basic map) is
 * byte-for-byte what it was: every node on floor 0, every vertical exit an
 * annotation, so nothing a pack pinned moves under it.
 *
 * Pure: no clock, no randomness, no I/O. Same `(bundle, state)`, same graph.
 *
 * @packageDocumentation
 */

import type { RoomId, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { MapGraph, MapGraphAnnotation, MapGraphEdge, MapGraphLift, MapGraphNode } from './map-graph.js';

import { visibleExitsOf } from '../execution/exit-visibility.js';

/**
 * The eight grid-placing directions and their offsets (screen coordinates:
 * north is up). D138 added the diagonals on Mike's ruling — the standard
 * vocabulary carries them, so a diagonal exit DRAWS rather than becoming a
 * labeled annotation. Everything else (up, down, in, out) annotates — except
 * up and down on a levelled graph (D169), which place a floor away.
 */
const CARDINAL_OFFSETS: ReadonlyMap<string, readonly [number, number]> = new Map([
	['east', [1, 0]],
	['north', [0, -1]],
	['northeast', [1, -1]],
	['northwest', [-1, -1]],
	['south', [0, 1]],
	['southeast', [1, 1]],
	['southwest', [-1, 1]],
	['west', [-1, 0]],
]);

/** The two floor-changing directions and their level offsets (D169). */
const VERTICAL_OFFSETS: ReadonlyMap<string, number> = new Map([
	['down', -1],
	['up', 1],
]);

/** The axis a drawn direction runs along on the floor plane — what the hologram turns a corridor by (D169, D175). */
export type MapDirectionAxis = 'diagonal' | 'x' | 'y';

/**
 * Which axis a direction's link lies along on the floor, or nothing for a
 * direction the map does not draw as a link (up, down, in, out, and any
 * word the layout has no offset for).
 *
 * @param directionToken - The exit's direction, as authored
 * @returns The axis, or undefined
 */
export const mapDirectionAxisOf = (directionToken: string): MapDirectionAxis | undefined => {
	const offset = CARDINAL_OFFSETS.get(directionToken);

	if (offset === undefined) {
		return undefined;
	}

	if (offset[1] === 0) {
		return 'x';
	}

	return offset[0] === 0 ? 'y' : 'diagonal';
};

/** What an unknown node and an unknown destination are called (never a real name). */
export const UNKNOWN_LABEL = '???';

/** How an exit leaving the pack is described in annotations. */
export const OUTWARD_LABEL = 'beyond this map';

/** How a graph is derived. */
export interface DeriveMapGraphOptions {
	/** D169: place `up` and `down` a floor away and draw them as lifts; otherwise they annotate, as always. */
	readonly isLevelled?: boolean;
}

/** A cell on a floor. */
interface Coordinate {
	readonly level: number;
	readonly x: number;
	readonly y: number;
}

/** A node under construction (the public node is frozen at assembly). */
type DraftNode = Omit<MapGraphNode, 'level' | 'x' | 'y'>;

/** Where one exit lands from a cell: a step on the floor, a floor away, or nowhere the grid can draw. */
type Step = { readonly kind: 'floor'; readonly offset: readonly [number, number]} | { readonly kind: 'lift'; readonly levels: number } | undefined;

/** The walk's state, threaded through the two passes. */
interface Walk {
	readonly annotations: MapGraphAnnotation[];
	readonly bundle: EngineGameBundle;
	readonly coordinatesByRoomId: Map<string, Coordinate>;
	readonly draftsByCoordinate: Map<string, DraftNode>;
	readonly edges: MapGraphEdge[];
	readonly isLevelled: boolean;
	readonly knownRoomIds: ReadonlySet<string>;
	readonly lifts: MapGraphLift[];
	readonly state: RuntimeState;
	readonly visitedRoomIds: ReadonlySet<string>;
}

/**
 * Derive the structured map for one session.
 *
 * @param bundle - The loaded bundle (rooms, exits, names)
 * @param state - The session state (visited, revealed, active room)
 * @param options - {@link DeriveMapGraphOptions}
 * @returns The graph every map renderer consumes
 */
export const deriveMapGraph = (bundle: EngineGameBundle, state: RuntimeState, options: DeriveMapGraphOptions = {}): MapGraph => {
	const visitedRoomIds = new Set<string>(state.progression.visitedRoomIds);
	const walk: Walk = {
		annotations: [],
		bundle,
		coordinatesByRoomId: new Map(),
		draftsByCoordinate: new Map(),
		edges: [],
		isLevelled: options.isLevelled === true,
		knownRoomIds: new Set<string>([...visitedRoomIds, ...state.mapDiscovery.revealedRoomIds, state.progression.activeRoomId]),
		lifts: [],
		state,
		visitedRoomIds,
	};

	placeKnownRooms(walk);
	connectPlacedRooms(walk);

	// The walk places the ACTIVE room at the origin (D34), so its levels are
	// relative to wherever the player stands — and a floor's number would
	// change every time they took the stairs. Floors are numbered from the
	// pack's entry room instead: where the story began is level 0, whatever
	// the player has climbed or dropped since. A flat walk has one level, so
	// this is the identity there and the flat graph stays byte-for-byte.
	const groundLevel = walk.coordinatesByRoomId.get(bundle.contentPack.manifest.entryRoomId)?.level ?? 0;
	const grounded = <T extends { readonly level: number }>(placed: T): T => ({ ...placed, level: placed.level - groundLevel });
	const nodes = [...walk.draftsByCoordinate].map(([key, draft]) => grounded({ ...draft, ...parseCoordinateKey(key) }));

	return {
		annotations: [...walk.annotations].sort((left, right) => annotationSortKey(left).localeCompare(annotationSortKey(right), 'en')),
		bounds: {
			maxLevel: Math.max(...nodes.map((node) => node.level)),
			maxX: Math.max(...nodes.map((node) => node.x)),
			maxY: Math.max(...nodes.map((node) => node.y)),
			minLevel: Math.min(...nodes.map((node) => node.level)),
			minX: Math.min(...nodes.map((node) => node.x)),
			minY: Math.min(...nodes.map((node) => node.y)),
		},
		edges: walk.edges.map(grounded),
		lifts: walk.lifts.map((lift) => ({ ...lift, fromLevel: lift.fromLevel - groundLevel, toLevel: lift.toLevel - groundLevel })),
		nodes,
		visitedCount: visitedRoomIds.size,
	};
};

/** Where an exit's direction lands from a cell, under the walk's rules. */
const stepOf = (walk: Walk, directionToken: string): Step => {
	const offset = CARDINAL_OFFSETS.get(directionToken);

	if (offset !== undefined) {
		return { kind: 'floor', offset };
	}

	const levels = VERTICAL_OFFSETS.get(directionToken);

	return walk.isLevelled && levels !== undefined ? { kind: 'lift', levels } : undefined;
};

/** The cell one step from another. */
const stepped = (from: Coordinate, step: NonNullable<Step>): Coordinate =>
	step.kind === 'floor' ? { level: from.level, x: from.x + step.offset[0], y: from.y + step.offset[1] } : { level: from.level + step.levels, x: from.x, y: from.y };

/**
 * Breadth-first placement of known rooms outward from the active room.
 *
 * @param walk - The walk: the grid and the placements it fills
 */
const placeKnownRooms = (walk: Walk): void => {
	const startRoomId = walk.state.progression.activeRoomId;
	const queue: string[] = [startRoomId];
	const origin: Coordinate = { level: 0, x: 0, y: 0 };

	walk.draftsByCoordinate.set(coordinateKey(origin), draftForRoom(walk, startRoomId, true));
	walk.coordinatesByRoomId.set(startRoomId, origin);

	// The drained queue IS the exit condition: `shift()` is typed `T | undefined`,
	// so a `while (queue.length > 0)` header forced an undefined-check inside the
	// body that no test could ever enter. Hoisting the shift makes the emptiness
	// the loop's own test — same walk, same order, one fewer phantom branch
	// (engineering_standards §14.1).
	let currentRoomId = queue.shift();

	while (currentRoomId !== undefined) {
		const currentCoordinate = walk.coordinatesByRoomId.get(currentRoomId);
		const currentRoom = walk.bundle.indexes.roomsById.get(currentRoomId as RoomId);

		// A room the pack does not define places nothing (a corrupt save yields a
		// thin map, never a thrown turn) — skipped by NOT entering this block, so
		// the tail shift below still runs and the walk cannot stall.
		if (currentCoordinate !== undefined && currentRoom !== undefined) {
			// D138: a hidden exit places nothing — no stub, no fog, no name.
			for (const exit of visibleExitsOf(currentRoom.exits, walk.state)) {
				const step = stepOf(walk, exit.directionToken);
				const targetRoomId = exit.targetRoomId;

				if (step === undefined || targetRoomId === undefined || !walk.knownRoomIds.has(targetRoomId) || walk.coordinatesByRoomId.has(targetRoomId)) {
					continue;
				}

				const targetCoordinate = stepped(currentCoordinate, step);

				if (walk.draftsByCoordinate.has(coordinateKey(targetCoordinate))) {
					continue; // Collision — the annotation pass still reports the link.
				}

				walk.draftsByCoordinate.set(coordinateKey(targetCoordinate), draftForRoom(walk, targetRoomId, false));
				walk.coordinatesByRoomId.set(targetRoomId, targetCoordinate);
				queue.push(targetRoomId);
			}
		}

		currentRoomId = queue.shift();
	}
};

/**
 * Build edges between grid-adjacent placed rooms and lifts between
 * floor-adjacent ones; place fog stubs; annotate everything the grid cannot
 * draw honestly.
 *
 * @param walk - The walk: the placements, and the links it fills
 */
const connectPlacedRooms = (walk: Walk): void => {
	for (const [roomId, coordinate] of walk.coordinatesByRoomId) {
		const room = walk.bundle.indexes.roomsById.get(roomId as RoomId);

		if (room === undefined) {
			continue;
		}

		for (const exit of visibleExitsOf(room.exits, walk.state)) {
			const step = stepOf(walk, exit.directionToken);

			if (exit.leadsOutOfPack === true) {
				walk.annotations.push({ directionToken: exit.directionToken, fromRoomName: room.name, toLabel: OUTWARD_LABEL });
				continue;
			}

			const targetRoomId = exit.targetRoomId;

			if (targetRoomId === undefined) {
				continue;
			}

			if (!walk.knownRoomIds.has(targetRoomId)) {
				if (step !== undefined && placeFogStub(walk, exit.directionToken, coordinate, stepped(coordinate, step))) {
					continue;
				}

				walk.annotations.push({ directionToken: exit.directionToken, fromRoomName: room.name, toLabel: UNKNOWN_LABEL });
				continue;
			}

			const targetCoordinate = walk.coordinatesByRoomId.get(targetRoomId);

			if (targetCoordinate !== undefined && step !== undefined && isSameCell(targetCoordinate, stepped(coordinate, step))) {
				// Draw each adjacency once, from the lesser endpoint.
				if (isLesser(coordinate, targetCoordinate)) {
					linkBetween(walk, exit.directionToken, coordinate, targetCoordinate);
				}

				continue;
			}

			walk.annotations.push({
				directionToken: exit.directionToken,
				fromRoomName: room.name,
				toLabel: walk.bundle.indexes.roomsById.get(targetRoomId)?.name ?? targetRoomId,
			});
		}
	}
};

/** An unknown stub one step into the dark, and the link to it — unless the cell is taken. Answers whether it was placed. */
const placeFogStub = (walk: Walk, directionToken: string, from: Coordinate, fog: Coordinate): boolean => {
	if (walk.draftsByCoordinate.has(coordinateKey(fog))) {
		return false;
	}

	walk.draftsByCoordinate.set(coordinateKey(fog), { isActive: false, isCheckpoint: false, kind: 'room', knowledge: 'unknown' });
	linkBetween(walk, directionToken, from, fog);

	return true;
};

/** One link: an edge on the floor, or a lift between floors. */
const linkBetween = (walk: Walk, directionToken: string, from: Coordinate, to: Coordinate): void => {
	if (from.level === to.level) {
		walk.edges.push({ directionToken, fromX: from.x, fromY: from.y, level: from.level, toX: to.x, toY: to.y });
	} else {
		walk.lifts.push({ directionToken, fromLevel: from.level, toLevel: to.level, x: from.x, y: from.y });
	}
};

const isSameCell = (left: Coordinate, right: Coordinate): boolean => left.x === right.x && left.y === right.y && left.level === right.level;

/** Whether a cell comes first — by x, then y, then floor — so each adjacency is drawn from one end only. */
const isLesser = (left: Coordinate, right: Coordinate): boolean => left.x < right.x || left.y < right.y || left.level < right.level;

/** Build the draft node for one known room. */
const draftForRoom = (walk: Walk, roomId: string, isActive: boolean): DraftNode => {
	const room = walk.bundle.indexes.roomsById.get(roomId as RoomId);

	return {
		isActive,
		isCheckpoint: room?.isCheckpoint === true,
		kind: room?.map?.kind ?? 'room',
		knowledge: walk.visitedRoomIds.has(roomId) ? 'visited' : 'revealed',
		name: room?.name ?? roomId,
		roomId: roomId as RoomId,
	};
};

/** The deterministic sort key for one annotation (direction, then room, then destination). */
const annotationSortKey = (annotation: MapGraphAnnotation): string =>
	`${annotation.directionToken} ${annotation.fromRoomName} ${annotation.toLabel}`;

/** One cell as a map key. */
const coordinateKey = (coordinate: Coordinate): string => `${String(coordinate.x)},${String(coordinate.y)},${String(coordinate.level)}`;

/** Parse a cell key back to numbers. */
const parseCoordinateKey = (key: string): Coordinate => {
	const [xText, yText, levelText] = key.split(',');

	return { level: Number(levelText), x: Number(xText), y: Number(yText) };
};
